import { Navigate, Route, Routes } from 'react-router-dom'
import ProtectedRoute from '../components/common/ProtectedRoute'
import ActivitySummaryWrong from '../components/user/ActivitySummaryWrong'
import LearningQuizOnly from '../components/user/LearningQuizOnly'
import Account from '../pages/admin/Account'
import Overview from '../pages/admin/Overview'
import Centers from '../pages/admin/Centers'
import CentersInfo from '../pages/admin/CentersInfo'
import Chapters from '../pages/admin/Chapters'
import Dashboard from '../pages/admin/Dashboard'
import Inquiries from '../pages/admin/Inquiries'
import InquiryDetail from '../pages/admin/BoardDetail'
import InquiryCreate from '../pages/admin/BoardCreate'
import LectureCreate from '../pages/admin/LectureCreate'
import LectureEdit from '../pages/admin/LectureEdit'
import Lectures from '../pages/admin/Lectures'
import QuizCreate from '../pages/admin/QuizCreate'
import QuizEdit from '../pages/admin/QuizEdit'
import SignIn from '../pages/admin/SignIn'
import NotFound from '../pages/common/NotFound'
import Unauthorized from '../pages/common/Unauthorized'
import DashboardManager from '../pages/manager/DashboardManager'
import Users from '../pages/manager/Users'
import AdminUsers from '../pages/admin/AdminUsers'
import ActivitySummary from '../pages/user/ActivitySummary'
import LevelTest from '../pages/user/LevelTest'
import Progress from '../pages/user/Progress'
import Solver from '../pages/user/Solver'
import Summary from '../pages/user/Summary'
import UserSignIn from '../pages/user/UserSignIn'
import { useAuthStore } from '../store/useAuthStore'
import { useUserStore } from '../store/useUserStore'
import Uploads from '../pages/admin/Uploads'
import Billing from '../pages/admin/Billing'
import Invoice from '../pages/admin/Invoice'
import BoardDetail from '../pages/admin/BoardDetail'
import UserSignUp from '../pages/user/UserSignUp'
import Landing from '../pages/user/Landing'
import Subscribe from '../pages/user/Subscribe'
import DashboardParents from '../pages/parents/DashboardParents'
import DashboardIndividual from '../pages/individual/DashboardIndividual'
import BillingIndividual from '../pages/individual/BillingIndividual'
import AvatarShop from '../pages/individual/AvatarShop'
import BadgeCollection from '../pages/individual/BadgeCollection'
import UserAccount from '../pages/user/UserAccount'
import Orders from '../pages/admin/Orders'
import PromoCodes from '../pages/admin/PromoCodes'
import SolverIndividual from '../pages/individual/SolverIndividual'
import Result from '../pages/individual/Result'
import StudentInfo from '../pages/manager/StudentInfo'
import UploadsTeacher from '../pages/teacher/UploadsTeacher'
import Reports from '../pages/admin/Reports'
import MyReports from '../pages/user/MyReports'

const AppRoutes = () => {
  const { isLoggedIn, user } = useAuthStore()
  const hasUserProfile = () => {
    return !!(user?.AVATAR && user?.NICKNAME)
  }

  const hasUserSub = () => {
    return user?.SUB === 'Y';
  };

  // 센터회원 첫 로그인 여부 체크 (아바타와 닉네임이 모두 없으면 첫 로그인으로 간주)
  const isFirstTimeUser = () => {
    return user?.STATUS === 'U' && !user?.AVATAR && !user?.NICKNAME
  }

  const isFirstTimeUser2 = () => {
    return String(user?.LEVEL_ID ?? '').startsWith('99')
  }

  return (
    <Routes>
      {/* 로그인 관련 페이지 - 인증 불필요 */}
      <Route
        path="/signin"
        element={isLoggedIn ? <Navigate to="/" replace /> : <SignIn />}
      />

      {/* 공통 페이지들 - 인증 불필요 */}
      <Route path="/progress" element={<Progress />} />
      <Route path="/activitySummary" element={<ActivitySummary />} />
      <Route path="/activitySummaryWrong" element={<ActivitySummaryWrong />} />
      <Route path="/learningQuizOnly" element={<LearningQuizOnly />} />
      <Route path="/solver" element={<Solver />} />
      <Route path="/summary" element={<Summary />} />
      <Route path="/result" element={<Result />} />
      {/* 유저 프로필 생성 페이지 */}
      <Route
        path="/userSignin"
        element={
          !isLoggedIn ? (
            <Navigate to="/signin" replace />
          ) : (user?.STATUS === 'U' || user?.STATUS === 'O') &&
            hasUserProfile() ? (
            <Navigate to="/solver" replace />
          ) : (
            <UserSignIn />
          )
        }
      />

      {/* 인증 불필요 페이지 */}
      <Route path="/notfound" element={<NotFound />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/signup" element={<UserSignUp />} />
      <Route path="/subscribe" element={<Subscribe />} />
      <Route path="/solver/individual" element={<SolverIndividual />} />

      {/* 모든 사용자 접근 가능 페이지 */}
      <Route path="/inquiries" element={<Inquiries />} />
      <Route path="/inquiries/create" element={<InquiryCreate />} />
      <Route path="/inquiries/detail/:id" element={<InquiryDetail />} />
      <Route path="/account" element={<Account />} />
      <Route path="/billing/individual" element={<BillingIndividual />} />

      {/* 어드민 & 매니저 */}
      <Route element={<ProtectedRoute allowedStatus={['A', 'M']} />}>
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/uploads" element={<Uploads />} />
        <Route path="/dashboard/manager" element={<DashboardManager />} />
        <Route path="/manager/users" element={<Users />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/studentInfo" element={<StudentInfo />} />
        <Route path="/admin/reports" element={<Reports />} />
      </Route>

      {/* 어드민 페이지 */}
      <Route element={<ProtectedRoute allowedStatus={['A']} />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/centers" element={<Centers />} />
        <Route path="/centers/info" element={<CentersInfo />} />
        <Route path="/chapters" element={<Chapters />} />
        <Route path="/lectures" element={<Lectures />} />
        <Route path="/lectures/quiz/edit/" element={<QuizEdit />} />
        <Route path="/lectures/lecture/edit/" element={<LectureEdit />} />
        <Route path="/lectures/lecture/create" element={<LectureCreate />} />
        <Route path="/lectures/quiz/create" element={<QuizCreate />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/invoice" element={<Invoice />} />
        <Route path="/promo-codes" element={<PromoCodes />} />
      </Route>

      {/* 대시보드 + 보상 시스템 — 개인(O) + 센터학생(U) 공용 */}
      <Route element={<ProtectedRoute allowedStatus={['O', 'U']} />}>
        <Route path="/dashboard/individual" element={<DashboardIndividual />} />
        <Route path="/avatar-shop" element={<AvatarShop />} />
        <Route path="/badge-collection" element={<BadgeCollection />} />
      </Route>

      {/* 학생 + 부모 공용 */}
      <Route element={<ProtectedRoute allowedStatus={['O', 'U', 'P']} />}>
        <Route path="/my-reports" element={<MyReports />} />
      </Route>

      {/* 개인 회원 전용 페이지 */}
      <Route element={<ProtectedRoute allowedStatus={['O']} />}>
        <Route path="/progress" element={<Progress />} />
        <Route path="/activitySummary" element={<ActivitySummary />} />
        <Route
          path="/activitySummaryWrong"
          element={<ActivitySummaryWrong />}
        />
        <Route path="/learningQuizOnly" element={<LearningQuizOnly />} />
        <Route path="/solver" element={<Solver />} />
        <Route path="/summary" element={<Summary />} />
        <Route path="/levelTest" element={<LevelTest />} />
      </Route>

      <Route element={<ProtectedRoute allowedStatus={['P']} />}>
        <Route path="/dashboard/parents" element={<DashboardParents />} />
      </Route>

      <Route element={<ProtectedRoute allowedStatus={['T']} />}>
        <Route path="/uploads/teacher" element={<UploadsTeacher />} />
      </Route>

      {/* 메인 리다이렉트 */}
      <Route
        path="/"
        element={
          <Navigate
            to={
              isLoggedIn
                ? user?.STATUS === 'U'
                  ? isFirstTimeUser2()
                    ? '/solver/individual' // 센터회원 첫 로그인시 레벨테스트
                    : isFirstTimeUser()
                      ? '/userSignin' : '/solver'
                  : user?.STATUS === 'M'
                  ? '/dashboard/manager'
                  : user?.STATUS === 'O'
                  ? hasUserSub() ? hasUserProfile() ? '/solver' : '/userSignin' : '/subscribe'
                  : user?.STATUS === 'P'
                  ? '/dashboard/parents'
                  : user?.STATUS === 'T'
                  ? '/uploads/teacher'
                  : '/dashboard'
                : '/signin'
            }
            replace
          />
        }
      />

      {/* 404 처리 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default AppRoutes
